import { useEffect, useRef } from 'react';
import classNames from 'classnames';

import { CurrencySymbolEnum } from '../../lib/enums/currency.enum';
import { marketIndexesArray } from '../../lib/store/fundamental/constants';
import { formatCurrencyBySymbol, formatNumber } from '../../lib/util/DataHelpers';

import useIsMobile from '../hooks/useIsMobile';
import useWheelMarqueeScroll from '../hooks/useWheelMarqueeScroll';

import { getMarqueeAnimationDelay, getTranslateXFromElement } from './helpers';
import { MarketIndexesProps } from './MarketIndexes.types';

import styles from './MarketIndexes.module.scss';

// Must match animation-duration values in MarketIndexes.module.scss
const MARQUEE_DURATION_DESKTOP = 25;
const MARQUEE_DURATION_MOBILE = 20;

const MarketIndexes = ({ indexes }: MarketIndexesProps) => {
  const { isMobile } = useIsMobile();
  const hasIndexes = indexes.length > 0;
  const wrapperRef = useRef<HTMLDivElement>(null);
  const marqueeRef = useRef<HTMLDivElement>(null);
  const isHoveredRef = useRef(false);
  const hasManualScrollRef = useRef(false);
  const scrollPositionRef = useRef(0);

  useWheelMarqueeScroll({
    wrapperRef,
    marqueeRef,
    scrollPositionRef,
    isInteractionActiveRef: isHoveredRef,
    hasManualScrollRef,
    isEnabled: hasIndexes && !isMobile,
  });

  useEffect(() => {
    const wrapper = wrapperRef.current;
    const marquee = marqueeRef.current;
    if (!wrapper || !marquee || !isMobile) return;

    let touchStartX = 0;
    let touchStartPosition = 0;

    const handleTouchStart = (e: TouchEvent) => {
      if (e.touches.length === 0) return;
      touchStartX = e.touches[0].clientX;
      hasManualScrollRef.current = true;

      const currentX = getTranslateXFromElement(marquee);
      scrollPositionRef.current = currentX;
      touchStartPosition = currentX;

      marquee.style.animation = 'none';
      marquee.style.transform = `translateX(${currentX}px)`;
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches.length === 0) return;
      if (e.cancelable) {
        e.preventDefault();
      }

      const deltaX = e.touches[0].clientX - touchStartX;
      const halfWidth = marquee.scrollWidth / 2;
      if (!halfWidth) return;

      let newPos = touchStartPosition + deltaX;
      while (newPos < -halfWidth) newPos += halfWidth;
      while (newPos > 0) newPos -= halfWidth;

      scrollPositionRef.current = newPos;
      marquee.style.transform = `translateX(${newPos}px)`;
    };

    const handleTouchEnd = () => {
      const currentX = scrollPositionRef.current;
      const delay = getMarqueeAnimationDelay({
        currentX,
        duration: MARQUEE_DURATION_MOBILE,
        scrollWidth: marquee.scrollWidth,
      });
      marquee.style.animation = '';
      marquee.style.animationDelay = `${delay}s`;
      marquee.style.transform = '';
      hasManualScrollRef.current = false;
    };

    wrapper.addEventListener('touchstart', handleTouchStart, { passive: true });
    wrapper.addEventListener('touchmove', handleTouchMove, { passive: false });
    wrapper.addEventListener('touchend', handleTouchEnd, { passive: true });
    wrapper.addEventListener('touchcancel', handleTouchEnd, { passive: true });

    // eslint-disable-next-line consistent-return
    return () => {
      wrapper.removeEventListener('touchstart', handleTouchStart);
      wrapper.removeEventListener('touchmove', handleTouchMove);
      wrapper.removeEventListener('touchend', handleTouchEnd);
      wrapper.removeEventListener('touchcancel', handleTouchEnd);
    };
  }, [hasIndexes, isMobile]);

  const handleMouseEnter = () => {
    const marquee = marqueeRef.current;
    if (!marquee || isMobile) return;

    isHoveredRef.current = true;

    const currentX = getTranslateXFromElement(marquee);
    scrollPositionRef.current = currentX;
  };

  const handleMouseLeave = () => {
    const marquee = marqueeRef.current;
    if (!marquee || isMobile) return;

    isHoveredRef.current = false;
    if (!hasManualScrollRef.current) return;

    const currentX = scrollPositionRef.current;
    const delay = getMarqueeAnimationDelay({
      currentX,
      duration: MARQUEE_DURATION_DESKTOP,
      scrollWidth: marquee.scrollWidth,
    });

    // Resume auto-scroll from the manually scrolled position
    marquee.style.animation = '';
    marquee.style.animationDelay = `${delay}s`;
    marquee.style.transform = '';
    hasManualScrollRef.current = false;
  };

  if (!hasIndexes) {
    return (
      <div className={styles.indexes}>
        <div className={styles.wrapper}>
          <div className={styles.loader} />
        </div>
      </div>
    );
  }

  const renderTrackCards = (keyPrefix: string) =>
    marketIndexesArray.map((index) => {
      const item = indexes.find(obj => obj.symbol === index.symbol);
      if (!item) return null;

      return (
        <div key={`${keyPrefix}${item.symbol}`} className={styles.card}>
          <div className={styles.dataRow}>
            <div className={styles.name}>
              {index.shownAs}
            </div>
            {!isMobile && (
              <div className={styles.price}>
                {formatCurrencyBySymbol(item.price, CurrencySymbolEnum.USD)}
              </div>
            )}
            {(item.changesPercentage ?? 0) > 0 ? (
              <div className={classNames(styles.percentageChange, styles.positivePercentage)}>
                (+
                {formatNumber(item.changesPercentage)}
                %)
              </div>
            ) : (
              <div className={classNames(styles.percentageChange, styles.negativePercentage)}>
                ({formatNumber(item.changesPercentage)}
                %)
              </div>
            )}
          </div>
        </div>
      );
    });

  return (
    <div className={styles.indexes}>
      <div
        ref={wrapperRef}
        className={classNames(styles.wrapper, styles.scrolling)}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div ref={marqueeRef} className={styles.marqueeContent}>
          <div className={styles.track}>
            {renderTrackCards('')}
          </div>
          <div className={styles.track} aria-hidden>
            {renderTrackCards('dup-')}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarketIndexes;
